// Leer y escribir los posts del calendario —
// ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts.
//
// Un post ES una fila de `lanzamientos_productos` con `producto_id` en null y
// `programado_at` puesto. Por eso acá no hay altas de medios ni de comentarios:
// los medios cuelgan de esa fila igual que las fotos de un producto
// (`sumarGeneradasAProducto`, `subirImagenAProducto`, `quitarImagen`,
// `reordenarImagenes`) y los comentarios también. Todo eso se reusa tal cual.
//
// Lo único propio del calendario es: el copy, la fecha, y qué pasa cuando llega
// la hora.

import { supabase } from "../../../lib/supabase";
import { esNombreDeVideo } from "../../../utils/posterDeVideo";
import { firmarThumb } from "../lanzamientosApi";
import type { PostEnAgenda } from "./mejorMomento";
import type { EstadoDePost, MedioDePost, PostDeRedes } from "./post";

/** Qué hacer cuando llega la hora de un post. */
export type ModoDePublicacion = "avisar" | "automatico";

interface FilaDeMedio {
  id: string;
  imagen_generada_id: string | null;
  file_name: string | null;
  orden: number | null;
  imagenes_generadas?: { file_name: string } | Array<{ file_name: string }> | null;
}

/** El nombre del archivo, venga de una generación o de una subida. */
function nombreDelArchivo(m: FilaDeMedio): string {
  if (m.file_name) return m.file_name;
  const g = Array.isArray(m.imagenes_generadas) ? m.imagenes_generadas[0] : m.imagenes_generadas;
  return g?.file_name ?? "";
}

/**
 * Qué decirle al usuario cuando la consulta falla.
 *
 * El caso que de verdad pasa hoy es que la migración no esté aplicada: PostgREST
 * contesta 400 con "column ... does not exist" y, sin traducirlo, la pantalla
 * mostraba un calendario vacío como si no hubiera posts. Nombrar la causa es lo
 * que evita buscarla en el lugar equivocado.
 */
function mensajeDeError(error: { message?: string; code?: string }): string {
  const m = error.message ?? "";
  if (/does not exist|42703|PGRST204/i.test(m + (error.code ?? ""))) {
    return "Falta aplicar la migración del calendario en la base (programado_at y modo_publicacion).";
  }
  return "No se pudo cargar el calendario.";
}

const SELECT_MEDIOS =
  "lanzamientos_imagenes(id, imagen_generada_id, file_name, orden, imagenes_generadas(file_name))";

/** Un post con sus medios y las miniaturas ya firmadas. */
export interface PostConMiniaturas extends PostDeRedes {
  /** Una URL firmada por medio, en el mismo orden. Vacía = no hay póster. */
  miniaturas: string[];
}

/**
 * Los posts, y si falló, POR QUÉ.
 *
 * El error se devuelve en vez de tragarse: una lista vacía porque no hay posts y
 * una lista vacía porque la consulta reventó se ven idénticas en pantalla, y esa
 * confusión ya costó una sesión entera de "no anda y no dice nada".
 */
export async function fetchPosts(
  lanzamientoId: string,
  companyId: string,
): Promise<{ posts: PostConMiniaturas[]; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select(`id, descripcion, programado_at, estado, error, ${SELECT_MEDIOS}`)
    .eq("lanzamiento_id", lanzamientoId)
    .eq("id_company", companyId)
    .order("programado_at", { ascending: true });
  if (error) {
    console.error("[postsApi] posts", error);
    return { posts: [], error: mensajeDeError(error) };
  }

  const posts = await Promise.all(
    ((data || []) as any[]).map(async (p) => {
      const crudos = ((p.lanzamientos_imagenes || []) as FilaDeMedio[])
        .slice()
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      const medios: MedioDePost[] = crudos.map((m, i) => {
        const fileName = nombreDelArchivo(m);
        return {
          id: m.id,
          generadaId: m.imagen_generada_id,
          fileName,
          esVideo: esNombreDeVideo(fileName),
          orden: i,
        };
      });
      const miniaturas = await Promise.all(
        medios.map((m) => (m.fileName ? firmarThumb(companyId, m.fileName) : "")),
      );
      return {
        id: p.id as string,
        cuando: p.programado_at ? new Date(p.programado_at as string) : null,
        texto: (p.descripcion as string) ?? "",
        medios,
        estado: (p.estado as EstadoDePost) ?? "pendiente",
        error: (p.error as string) ?? null,
        miniaturas,
      };
    }),
  );
  return { posts, error: null };
}

/**
 * Todo lo que la EMPRESA tiene planificado, de todos sus lanzamientos de redes.
 *
 * Es contra esto que el sugeridor evita pisarse. Mirar sólo el lanzamiento
 * abierto dejaba que dos lanzamientos en paralelo cayeran el mismo día y nadie
 * se enteraba hasta que salían.
 *
 * Trae sólo lo que todavía no salió: lo que ya se publicó ocupó su lugar en el
 * pasado y no compite por un hueco futuro.
 */
export async function fetchAgendaDeLaEmpresa(
  companyId: string,
): Promise<{ agenda: PostEnAgenda[]; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select("id, programado_at")
    .eq("id_company", companyId)
    .not("programado_at", "is", null)
    .neq("estado", "publicado")
    .order("programado_at", { ascending: true });
  if (error) {
    console.error("[postsApi] agenda", error);
    return { agenda: [], error: mensajeDeError(error) };
  }
  return {
    agenda: ((data || []) as Array<{ id: string; programado_at: string }>).map((r) => ({
      id: r.id,
      cuando: new Date(r.programado_at),
    })),
    error: null,
  };
}

export async function crearPost(
  lanzamientoId: string,
  companyId: string,
  cuando: Date,
): Promise<{ id: string | null; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .insert({
      lanzamiento_id: lanzamientoId,
      id_company: companyId,
      // Sin producto: un post no sale del catálogo. Es lo que la migración
      // 20260915120000 habilitó.
      producto_id: null,
      programado_at: cuando.toISOString(),
      descripcion: "",
    })
    .select("id")
    .single();
  if (error || !data) {
    console.error("[postsApi] crear post", error);
    return { id: null, error: "No se pudo crear el post" };
  }
  return { id: (data as { id: string }).id, error: null };
}

export async function guardarPost(
  id: string,
  campos: { texto?: string; cuando?: Date | null },
): Promise<{ error: string | null }> {
  const cambios: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (campos.texto !== undefined) cambios.descripcion = campos.texto;
  if (campos.cuando !== undefined) {
    cambios.programado_at = campos.cuando ? campos.cuando.toISOString() : null;
  }
  const { error } = await supabase.from("lanzamientos_productos").update(cambios).eq("id", id);
  if (error) {
    console.error("[postsApi] guardar post", error);
    return { error: "No se pudo guardar" };
  }
  return { error: null };
}

export async function borrarPost(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lanzamientos_productos").delete().eq("id", id);
  if (error) {
    console.error("[postsApi] borrar post", error);
    return { error: "No se pudo borrar el post" };
  }
  return { error: null };
}

// ── Cómo sale: aviso o automático ──────────────────────────────────────────

export async function fetchModoPublicacion(lanzamientoId: string): Promise<ModoDePublicacion> {
  // Sin la columna esto devuelve error y `data` null: se cae a "avisar", que es
  // el default de la tabla. El aviso del problema lo da la carga de los posts.
  const { data } = await supabase
    .from("lanzamientos")
    .select("modo_publicacion")
    .eq("id", lanzamientoId)
    .maybeSingle();
  return ((data as { modo_publicacion?: string } | null)?.modo_publicacion ?? "avisar") as
    ModoDePublicacion;
}

export async function setModoPublicacion(
  lanzamientoId: string,
  modo: ModoDePublicacion,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("lanzamientos")
    .update({ modo_publicacion: modo, updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  if (error) {
    console.error("[postsApi] modo de publicación", error);
    return { error: "No se pudo cambiar el modo" };
  }
  return { error: null };
}

/**
 * La cuenta de Instagram conectada, si hay.
 *
 * Es la MISMA fuente que usa Configuración (`instagram_data` por empresa), para
 * que el calendario no pueda decir "conectada" mientras la otra pantalla dice
 * que no.
 */
export async function fetchCuentaDeInstagram(companyId: string): Promise<string | null> {
  const { data } = await supabase
    .from("instagram_data")
    .select("instagram_username")
    .eq("id_company", companyId)
    .maybeSingle();
  const u = (data as { instagram_username?: string } | null)?.instagram_username;
  return u && u.trim() ? u : null;
}
